import { SCULPT } from "@/config/designer";
import type { VoxelField } from "./VoxelField";

export type BrushKind = "carve" | "smooth";

/** One dab of a stroke. Strokes are resampled into evenly spaced dabs. */
export interface BrushDab {
  readonly kind: BrushKind;
  /** Centre, mm. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Radius of influence, mm. */
  readonly radius: number;
  /**
   * Carve: millimetres of material removed at the centre of the dab.
   * Smooth: 0..1 blend towards the local average.
   */
  readonly amount: number;
  /**
   * How downward-facing the stroke is (0..1, from the surface normal's y).
   * Only downward strokes are held off the base — carving the SIDES of the
   * block may go all the way to the mounting plane, so a hold is never left
   * with a full-footprint flange around its base.
   */
  readonly topness: number;
  /** Optional floor protection applied equally to top and side strokes. */
  readonly lockedBaseMm?: number;
}

/** Inclusive-exclusive sample index range. */
export interface SampleRegion {
  i0: number;
  i1: number;
  j0: number;
  j1: number;
  k0: number;
  k1: number;
}

export function unionRegion(a: SampleRegion | null, b: SampleRegion | null): SampleRegion | null {
  if (!a) return b;
  if (!b) return a;
  return {
    i0: Math.min(a.i0, b.i0),
    i1: Math.max(a.i1, b.i1),
    j0: Math.min(a.j0, b.j0),
    j1: Math.max(a.j1, b.j1),
    k0: Math.min(a.k0, b.k0),
    k1: Math.max(a.k1, b.k1),
  };
}

/** Sample indices a dab can possibly touch, clipped to the grid. */
export function dabRegion(field: VoxelField, dab: BrushDab, expand = 0): SampleRegion {
  const r = dab.radius + expand * field.h;
  const lo = (v: number, o: number, n: number): number =>
    Math.max(0, Math.min(n - 1, Math.floor((v - r - o) / field.h)));
  const hi = (v: number, o: number, n: number): number =>
    Math.max(0, Math.min(n - 1, Math.ceil((v + r - o) / field.h)));
  return {
    i0: lo(dab.x, field.ox, field.nx),
    i1: hi(dab.x, field.ox, field.nx) + 1,
    j0: lo(dab.y, field.oy, field.ny),
    j1: hi(dab.y, field.oy, field.ny) + 1,
    k0: lo(dab.z, field.oz, field.nz),
    k1: hi(dab.z, field.oz, field.nz) + 1,
  };
}

/** Smooth, C1 falloff: 1 at the centre, 0 with zero slope at the rim. */
function falloff(t2: number): number {
  const u = 1 - t2;
  return u * u;
}

/**
 * The base guard. Returns the height below which a downward stroke may not
 * remove any more material. Zero for side strokes.
 */
function guardHeight(topness: number, lockedBaseMm = 0): number {
  return Math.max(
    lockedBaseMm,
    SCULPT.minBaseThickness * Math.max(0, Math.min(1, topness)),
  );
}

/**
 * Carve: erode the field outwards. `data` only ever increases, which is what
 * guarantees the solid stays a subset of the original blank — and therefore
 * that the rear face can never bulge below the mounting plane.
 */
export function applyCarve(field: VoxelField, dab: BrushDab): SampleRegion | null {
  const reg = dabRegion(field, dab);
  if (reg.i0 >= reg.i1 || reg.j0 >= reg.j1 || reg.k0 >= reg.k1) return null;

  const { data, dirty, clamp } = field;
  const r2 = dab.radius * dab.radius;
  const floorY = guardHeight(dab.topness, dab.lockedBaseMm);
  let touched = false;

  for (let k = reg.k0; k < reg.k1; k++) {
    const dz = field.sampleZ(k) - dab.z;
    const dz2 = dz * dz;
    if (dz2 > r2) continue;
    for (let j = reg.j0; j < reg.j1; j++) {
      const y = field.sampleY(j);
      const dy = y - dab.y;
      const dyz2 = dz2 + dy * dy;
      if (dyz2 > r2) continue;
      const guarded = floorY > 0 && y < floorY;
      const guardValue = guarded ? -(floorY - y) : 0;
      const row = field.index(0, j, k);
      for (let i = reg.i0; i < reg.i1; i++) {
        const dx = field.sampleX(i) - dab.x;
        const d2 = dyz2 + dx * dx;
        if (d2 > r2) continue;

        const p = row + i;
        const prev = data[p] as number;
        let next = prev + dab.amount * falloff(d2 / r2);
        if (guarded) {
          // Never thin the base past the guard, but never restore material
          // that has already gone either.
          const limit = prev > guardValue ? prev : guardValue;
          if (next > limit) next = limit;
        }
        if (next > clamp) next = clamp;
        if (next > prev) {
          data[p] = next;
          dirty[p] = 1;
          touched = true;
        }
      }
    }
  }
  return touched ? reg : null;
}

/**
 * Smooth: blend the field towards its local average. Bounded by the original
 * blank so it can never grow material beyond the foam block, and held off the
 * bottom voxel layer so the mounting plane's field stays pristine.
 */
export function applySmooth(field: VoxelField, dab: BrushDab): SampleRegion | null {
  const reg = dabRegion(field, dab);
  if (reg.i0 >= reg.i1 || reg.j0 >= reg.j1 || reg.k0 >= reg.k1) return null;

  const { data, dirty, nx, ny, nz } = field;

  // Read from an untouched copy so the blur has no directional bias.
  const s = {
    i0: Math.max(0, reg.i0 - 1),
    i1: Math.min(nx, reg.i1 + 1),
    j0: Math.max(0, reg.j0 - 1),
    j1: Math.min(ny, reg.j1 + 1),
    k0: Math.max(0, reg.k0 - 1),
    k1: Math.min(nz, reg.k1 + 1),
  };
  const sw = s.i1 - s.i0;
  const sh = s.j1 - s.j0;
  const sd = s.k1 - s.k0;
  const src = new Float32Array(sw * sh * sd);
  for (let k = 0; k < sd; k++) {
    for (let j = 0; j < sh; j++) {
      const from = field.index(s.i0, s.j0 + j, s.k0 + k);
      src.set(data.subarray(from, from + sw), (k * sh + j) * sw);
    }
  }
  const readSrc = (i: number, j: number, k: number): number => {
    const ci = i < s.i0 ? s.i0 : i >= s.i1 ? s.i1 - 1 : i;
    const cj = j < s.j0 ? s.j0 : j >= s.j1 ? s.j1 - 1 : j;
    const ck = k < s.k0 ? s.k0 : k >= s.k1 ? s.k1 - 1 : k;
    return src[((ck - s.k0) * sh + (cj - s.j0)) * sw + (ci - s.i0)] as number;
  };

  const r2 = dab.radius * dab.radius;
  const floorY = guardHeight(dab.topness, dab.lockedBaseMm);
  const minY = field.h; // keep one voxel above the mounting plane pristine
  let touched = false;

  for (let k = reg.k0; k < reg.k1; k++) {
    const z = field.sampleZ(k);
    const dz = z - dab.z;
    const dz2 = dz * dz;
    if (dz2 > r2) continue;
    for (let j = reg.j0; j < reg.j1; j++) {
      const y = field.sampleY(j);
      if (y < minY) continue;
      const dy = y - dab.y;
      const dyz2 = dz2 + dy * dy;
      if (dyz2 > r2) continue;
      const guarded = floorY > 0 && y < floorY;
      const guardValue = guarded ? -(floorY - y) : 0;
      const row = field.index(0, j, k);
      for (let i = reg.i0; i < reg.i1; i++) {
        const x = field.sampleX(i);
        const dx = x - dab.x;
        const d2 = dyz2 + dx * dx;
        if (d2 > r2) continue;

        const p = row + i;
        const prev = data[p] as number;
        const avg =
          (readSrc(i - 1, j, k) +
            readSrc(i + 1, j, k) +
            readSrc(i, j - 1, k) +
            readSrc(i, j + 1, k) +
            readSrc(i, j, k - 1) +
            readSrc(i, j, k + 1) +
            readSrc(i, j, k) * 2) /
          8;

        const w = dab.amount * falloff(d2 / r2);
        let next = prev + (avg - prev) * w;

        // Never exceed the original blank.
        const blank = field.blankDistance(x, y, z);
        if (next < blank) next = blank;
        if (guarded && next > prev) {
          const limit = prev > guardValue ? prev : guardValue;
          if (next > limit) next = limit;
        }
        next = field.clampValue(next);

        if (next !== prev) {
          data[p] = next;
          dirty[p] = 1;
          touched = true;
        }
      }
    }
  }
  return touched ? reg : null;
}

export function applyDab(field: VoxelField, dab: BrushDab): SampleRegion | null {
  return dab.kind === "carve" ? applyCarve(field, dab) : applySmooth(field, dab);
}

/**
 * Resample a pointer movement into evenly spaced dabs, so a fast drag carves
 * the same continuous groove as a slow one.
 */
export function buildStrokeDabs(
  from: { x: number; y: number; z: number; topness: number } | null,
  to: { x: number; y: number; z: number; topness: number },
  kind: BrushKind,
  radius: number,
  amount: number,
  lockedBaseMm = 0,
): BrushDab[] {
  const make = (x: number, y: number, z: number, topness: number): BrushDab => ({
    kind,
    x,
    y,
    z,
    radius,
    amount,
    topness,
    lockedBaseMm,
  });
  if (!from) return [make(to.x, to.y, to.z, to.topness)];

  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const dist = Math.hypot(dx, dy, dz);
  const spacing = Math.max(radius * SCULPT.strokeSpacingFraction, 0.35);
  const steps = Math.min(64, Math.max(1, Math.ceil(dist / spacing)));

  const dabs: BrushDab[] = [];
  for (let n = 1; n <= steps; n++) {
    const t = n / steps;
    dabs.push(
      make(
        from.x + dx * t,
        from.y + dy * t,
        from.z + dz * t,
        from.topness + (to.topness - from.topness) * t,
      ),
    );
  }
  return dabs;
}
